import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, ChevronDown } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eyebrow } from "@/components/sg/Brand";

const EXAMPLES = [
  "Remodeling Sarah Lee's kitchen for $5,000 total. $1,500 down payment upfront, $2,000 after drywall installation, $1,500 when cabinet handles are installed.",
  "Website redesign for Peak Fitness, $3,600. 30% deposit, $1,200 after homepage design is approved, the rest at launch.",
  "Deck build for Tom Alvarez, $8,400: materials deposit $2,400, framing complete $3,000, final inspection and staining $3,000.",
];

export function IntakeCard({ onCreated, onUpgrade }) {
  const [description, setDescription] = useState("");
  const [showContact, setShowContact] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (description.trim().length < 15) { toast.error("Add a bit more detail: client, total price and payment steps."); return; }
    setLoading(true);
    try {
      const r = await api.post("/projects", { description, client_email: email || null, client_phone: phone || null });
      setDescription(""); setEmail(""); setPhone("");
      toast.success("Milestone blueprint ready");
      onCreated(r.data);
    } catch (e) {
      const msg = formatErr(e.response?.data?.detail) || e.message;
      if (e.response?.status === 402) toast.error(msg, { action: { label: "Upgrade", onClick: onUpgrade } });
      else toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <Eyebrow>Quick job intake</Eyebrow>
        <h2 className="font-display text-2xl md:text-3xl font-bold mt-2">Describe the job in your own words</h2>
        <p className="text-sm text-muted-foreground mt-2">Include the client, total price and when you get paid. AI does the rest.</p>
      </div>
      <div className="border border-border rounded-xl bg-card p-5 focus-within:ring-2 focus-within:ring-indigo-500/50 transition-shadow">
        <Textarea data-testid="job-description-textarea" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., Remodeling Sarah's kitchen for $5,000 total..."
          className="min-h-[180px] border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 resize-none p-0 text-base leading-relaxed shadow-none" />
        <div className="flex flex-wrap gap-2 mt-3">
          {EXAMPLES.map((ex, i) => (
            <button key={i} data-testid={`intake-example-${i}`} onClick={() => setDescription(ex)}
              className="text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-indigo-500/50 transition-colors">
              {["Kitchen remodel", "Web design", "Deck build"][i]}
            </button>
          ))}
        </div>
        <button data-testid="intake-contact-toggle" onClick={() => setShowContact(!showContact)} className="mt-4 text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ChevronDown className={`h-3 w-3 transition-transform ${showContact ? "rotate-180" : ""}`} /> Add client contact (optional)
        </button>
        {showContact && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><Label className="text-xs">Client email</Label><Input data-testid="intake-client-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" /></div>
            <div><Label className="text-xs">Client mobile</Label><Input data-testid="intake-client-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+15551234567" /></div>
          </div>
        )}
        <Button data-testid="generate-blueprint-btn" disabled={loading} onClick={generate} className="w-full mt-4 h-11 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold">
          <Sparkles className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Building your blueprint…" : "Generate Milestone Blueprint"}
        </Button>
      </div>
    </div>
  );
}
